import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
} from '@mui/material';

import api from '../../utils/functions/api';
import { showAlert, confirmAsync } from '../../utils/functions/globalDialog';
import * as actions from '../../redux/actions';
import { ExpensesTableApplove } from '../../components/Table';
import ExpensesFilterPanel from '../../components/Card/ExpensesFilterPanel';
import { buildExpensesQuery } from '../../utils/functions/buildExpensesQuery';

const tabKey = 'labor';

// ชุด chip กรองสถานะของแท็บนี้ — รายการค่าแรง auto-sync เดินผ่าน flow อนุมัติปกติทุกสถานะได้ (ไม่มี LABOR_DRAFT
// คั่นแยกแล้ว ดูคอมเมนต์ท้ายไฟล์) เจอได้ทุก status เหมือน tab "ทั้งหมด" เลยใช้ชุดเดียวกัน (ไม่มี CANCEL
// อีกแล้ว — ย้ายไปอยู่ในถังขยะที่ ExpensesTabRejected.js เท่านั้น ตาม getTabMatch ฝั่ง backend)
const STATUS_OPTIONS = [
  { key: 'ALL', label: 'ทั้งหมด', color: 'default', statuses: [] },
  { key: 'PENDING', label: 'Pending', color: 'warning', statuses: ['PENDING', 'PREAPPROVE', 'APPROVE_1', 'APPROVE_2'] },
  { key: 'APPROVE', label: 'Approved', color: 'warning', statuses: ['APPROVE'] },
  { key: 'PREPARE', label: 'Prepared', color: 'info', statuses: ['PREPARE'] },
  { key: 'SUCCESS', label: 'Success', color: 'success', statuses: ['SUCCESS'] },
  { key: 'REJECTED', label: 'Rejected', color: 'error', statuses: ['REJECT', 'REJECT_2', 'REJECT_3'] },
];

// tab "ตั้งเบิกค่าแรง" — Expenses ที่ isLabor=true ทุก status (สร้างเฉพาะตอนกดบันทึกเงินเดือน ผ่าน
// submitLaborDraftsToPending → status PENDING แล้วเดิน flow อนุมัติปกติเหมือนรายการเบิกทั่วไป) กล่อง
// "ตัวกรอง" ใช้ ExpensesFilterPanel (shared component แก้ layout ทีเดียวมีผลทุก tab) ต่างจาก tab อื่นตรง
// ไม่ทำ pagination จริง (ค่าแรงมีแค่ 1 แถวต่อโครงการต่องวด ไม่ใช่ตารางที่โตไม่จำกัดเหมือนรายจ่ายทั่วไป —
// ขอมาให้หมดในทีเดียวพอในทุกครั้งที่กรอง) และมีปุ่มแก้ไขงบประมาณ/ผู้รับเงิน + ปุ่มรีเซ็ต (ลบแล้วสร้างใหม่
// จาก snapshot เงินเดือนของงวดนั้น) เป็นของเฉพาะ tab นี้
export default function ExpensesTabLabor({ refreshTick = 0, onDoubleClickProject }) {
  const dispatch = useDispatch();
  const me = useSelector((state) => state.me);
  const info = useSelector((state) => state.info);
  const projects = useSelector((state) => state.project);
  const budgetState = useSelector((state) => state.budget);

  const [dateBounds, setDateBounds] = useState({ min: null, max: null });
  const [filterOpen, setFilterOpen] = useState(true);
  const [maxLookbackText, setMaxLookbackText] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [expensesData, setExpensesData] = useState({ rows: [], total: 0, isLoading: false });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [projectSelect, setProjectSelect] = useState(null);
  const [budgetSelect, setBudgetSelect] = useState(null);
  const [filterVat, setFilterVat] = useState(false);
  const [filterWithholding, setFilterWithholding] = useState(false);
  // เลือกได้หลาย chip พร้อมกัน (multi-select) — array ว่าง = "ทั้งหมด" ไม่กรอง
  const [statusFilters, setStatusFilters] = useState([]);
  const toggleStatusFilter = (key) => {
    if (key === 'ALL') { setStatusFilters([]); return; }
    setStatusFilters((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };
  const [dateFilterField, setDateFilterField] = useState('date');
  const [appliedDateFilterField, setAppliedDateFilterField] = useState('date');
  const [dateRange, setDateRange] = useState(null);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  const [advSearchName, setAdvSearchName] = useState('');
  const [advSearchCode, setAdvSearchCode] = useState('');
  const [advSearchPayee, setAdvSearchPayee] = useState('');
  const [advSearchApplicant, setAdvSearchApplicant] = useState('');
  const [advSearchNetAmount, setAdvSearchNetAmount] = useState('');

  const [editRow, setEditRow] = useState(null);
  const [budgetOptions, setBudgetOptions] = useState([]);
  const [editBudget, setEditBudget] = useState(null);
  const [editPayee, setEditPayee] = useState({ name: '', bank: '', account_number: '' });
  const [saving, setSaving] = useState(false);

  const dateRangeRef = useRef(null);

  useEffect(() => {
    dispatch(actions.projectAll({ lite: true }));
  }, [dispatch]);

  // ช่วงวันที่จริงของรายการค่าแรงเท่านั้น (ไม่ใช่ทั้งระบบ) — ใช้ bound date-range picker เหมือน tab อื่น
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`${process.env.REACT_APP_API_URL}/expenses/date-range?tab=${tabKey}&field=${dateFilterField}`);
        setDateBounds({
          min: data?.min ? new Date(data.min) : null,
          max: data?.max ? new Date(data.max) : null,
        });
      } catch (error) {
        console.error(error);
      }
    })();
  }, [dateFilterField]);

  useEffect(() => {
    if (projectSelect?._id) {
      dispatch(actions.budgetAll({ project_id: projectSelect._id }));
    }
  }, [dispatch, projectSelect]);

  // ค่าแรง auto-sync มีแค่ 1 แถวต่อโครงการต่องวด ไม่ใช่ตารางที่โตไม่จำกัด — ขอมาให้หมดในทีเดียวพอทุกครั้งที่
  // กรอง (ไม่ทำ pagination จริง เพราะ onLoadMore/hasMore ที่ส่งเข้า ExpensesTableApplove ด้านล่างยังเป็นแค่ placeholder)
  const fetchData = useCallback(async (overrides = {}) => {
    const searchVal = overrides.search ?? search;
    const dateRangeVal = overrides.dateRange ?? dateRange;
    setExpensesData((prev) => ({ ...prev, isLoading: true }));
    try {
      const q = buildExpensesQuery({
        tab: tabKey,
        page: 1,
        size: 999999,
        sortBy,
        sortDir,
        search: searchVal,
        projectId: projectSelect?._id,
        budgetId: budgetSelect?._id,
        filterVat,
        filterWithholding,
        statusFilters,
        statusOptions: STATUS_OPTIONS,
        dateRange: dateRangeVal,
        dateFilterField,
        advSearch: {
          name: advSearchName,
          code: advSearchCode,
          payee: advSearchPayee,
          applicant: advSearchApplicant,
          netAmount: advSearchNetAmount,
        },
      });
      const { data } = await api.get(`${process.env.REACT_APP_API_URL}/expenses?${q.toString()}`);
      setExpensesData({ rows: data?.rows || [], total: data?.total || 0, isLoading: false });
    } catch (error) {
      console.error(error);
      setExpensesData((prev) => ({ ...prev, isLoading: false }));
    }
  }, [
    search, projectSelect, budgetSelect, filterVat, filterWithholding, statusFilters,
    dateRange, dateFilterField, advSearchName, advSearchCode, advSearchPayee, advSearchApplicant,
    advSearchNetAmount, sortBy, sortDir,
  ]);

  // ใช้ ref เก็บ fetchData ตัวล่าสุดไว้เรียกจาก effect โดยไม่ให้ effect ต้อง re-run ทุกครั้งที่ filter
  // (search/project/budget/tax/dateRange) เปลี่ยน เพราะฟิลเตอร์พวกนี้ต้องรอกดปุ่ม "ค้นหา" เท่านั้นถึงจะ apply
  // — ยกเว้น refreshTick/statusFilters/sortBy/sortDir ที่ยัง apply ทันที เหมือนปุ่มสถานะ/เรียงตามใน tab อื่น
  const fetchDataRef = useRef(fetchData);
  fetchDataRef.current = fetchData;

  useEffect(() => {
    fetchDataRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTick, statusFilters, sortBy, sortDir]);

  const handleSearch = () => {
    const nextSearch = searchInput;
    // ห้ามใช้ `?? dateRange` ตรงนี้ — โหมด "ทั้งหมด" ใน ExpensesDateRangePicker ตั้งใจให้ getValue() คืน null
    // เพื่อสื่อว่า "ไม่กรองวันที่เลย" แต่ ?? จะเห็น null แล้ว fallback ไปใช้ dateRange อันเก่าแทน
    const nextDateRange = dateRangeRef.current?.getValue ? dateRangeRef.current.getValue() : dateRange;
    setSearch(nextSearch);
    setDateRange(nextDateRange);
    setAppliedDateFilterField(dateFilterField);
    fetchData({ search: nextSearch, dateRange: nextDateRange });
  };

  // ปุ่ม "โหลดใหม่" โผล่ให้ทุกคนที่เข้าถึงแท็บนี้เห็น เฉพาะรายการ isLabor=true ที่ยัง status PENDING เท่านั้น
  // ใช้ได้ทั้งงวดที่ยังแก้ไขได้และงวดที่บันทึกเงินเดือนไปแล้ว (resetLaborExpense ฝั่ง backend ไม่บล็อกงวด
  // locked แล้ว — สอดคล้องกับปุ่ม "สร้างรายการ" ใน TimestampProjectCost.js ที่อนุญาตงวด locked มาตั้งแต่แรก)
  const rowsWithReset = useMemo(() => (expensesData.rows || []).map((row) => ({
    ...row,
    canReset: !!row.isLabor && row.status === 'PENDING',
  })), [expensesData.rows]);

  // ── ปุ่ม "รีเซ็ต" ต่อรายการ — ลบรายการนี้ทิ้งแล้วให้ backend สร้างใหม่จาก snapshot ที่บันทึกเงินเดือนไว้จริง
  // (SalaryList.dashboard ของงวดเดียวกัน) ในคำขอเดียว — ไม่ต้อง mount HrReportV2 headless คำนวณสดอีกต่อไป
  // (รายการเบิกค่าแรงตอนนี้เกิดเฉพาะตอนกดบันทึกเงินเดือน = งวดที่มีรายการ = ปิดงวดแล้วเสมอ → snapshot มีเสมอ)
  const handleResetRow = async (row) => {
    if (!(await confirmAsync('ต้องการรีเซ็ตรายการนี้หรือไม่? รายการเดิมจะถูกลบแล้วสร้างใหม่จากยอดที่บันทึกเงินเดือนไว้ของงวดนั้น'))) {
      return;
    }
    try {
      const result = await dispatch(actions.resetLaborExpense(row._id));
      if (result && result.recreated === false) {
        showAlert('รีเซ็ตรายการแล้ว แต่ยังไม่พบข้อมูลเงินเดือนที่บันทึกไว้ของงวดนี้ — กรุณาบันทึกเงินเดือนงวดนั้นก่อน รายการเบิกจะถูกสร้างให้อัตโนมัติ');
      }
    } catch (error) {
      showAlert(error?.response?.data?.error?.message || error?.message || 'รีเซ็ตไม่สำเร็จ', { status: error?.response?.status });
    } finally {
      fetchData();
    }
  };

  const openEdit = async (row) => {
    setEditRow(row);
    setEditBudget(row.budget || null);
    setEditPayee({
      name: row.payee?.name || '',
      bank: row.payee?.bank || '',
      account_number: row.payee?.account_number || '',
    });
    try {
      const projectId = row.project?._id || row.project?.id || '';
      const { data } = await api.get(`${process.env.REACT_APP_API_URL}/budget?project_id=${projectId}&size=999999&page=1`);
      setBudgetOptions(data?.rows || []);
    } catch (error) {
      console.error(error);
      setBudgetOptions([]);
    }
  };

  const closeEdit = () => setEditRow(null);

  const saveEdit = async () => {
    if (!editRow) return;
    setSaving(true);
    try {
      await dispatch(actions.expensesPut(editRow._id, {
        budget: editBudget?._id || editBudget?.id || null,
        payee: editPayee,
      }));
      closeEdit();
      fetchData();
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleClearAdvanced = () => {
    setAdvSearchName('');
    setAdvSearchCode('');
    setAdvSearchPayee('');
    setAdvSearchApplicant('');
    setAdvSearchNetAmount('');
    setProjectSelect(null);
    setBudgetSelect(null);
    setFilterVat(false);
    setFilterWithholding(false);
  };

  return (
    <Box>
      <ExpensesFilterPanel
        filterOpen={filterOpen}
        onToggleFilterOpen={() => setFilterOpen((v) => !v)}
        maxLookbackText={maxLookbackText}
        showDateRange
        dateRangeRef={dateRangeRef}
        dateRange={dateRange}
        dateBounds={dateBounds}
        dateFilterField={dateFilterField}
        onDateFieldChange={setDateFilterField}
        onMaxLookbackChange={setMaxLookbackText}
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        onSearch={handleSearch}
        onClearSearch={() => { setSearchInput(''); setSearch(''); fetchData({ search: '' }); }}
        advancedOpen={advancedOpen}
        onToggleAdvanced={() => setAdvancedOpen((v) => !v)}
        projects={projects}
        projectSelect={projectSelect}
        onProjectChange={(val) => { setProjectSelect(val); setBudgetSelect(null); }}
        budgets={budgetState}
        budgetSelect={budgetSelect}
        onBudgetChange={setBudgetSelect}
        filterVat={filterVat}
        onFilterVatChange={setFilterVat}
        filterWithholding={filterWithholding}
        onFilterWithholdingChange={setFilterWithholding}
        advSearchName={advSearchName}
        onAdvSearchNameChange={setAdvSearchName}
        advSearchCode={advSearchCode}
        onAdvSearchCodeChange={setAdvSearchCode}
        advSearchPayee={advSearchPayee}
        onAdvSearchPayeeChange={setAdvSearchPayee}
        advSearchApplicant={advSearchApplicant}
        onAdvSearchApplicantChange={setAdvSearchApplicant}
        advSearchNetAmount={advSearchNetAmount}
        onAdvSearchNetAmountChange={setAdvSearchNetAmount}
        onClearAdvanced={handleClearAdvanced}
        appliedDateFilterField={appliedDateFilterField}
        appliedDateRange={dateRange}
      />

      <ExpensesTableApplove
        expenses={{ ...expensesData, rows: rowsWithReset }}
        paginationMode="infinite"
        onLoadMore={() => {}}
        hasMore={false}
        highlightQuery={search}
        filter={rowsWithReset}
        statusFilters={statusFilters}
        onStatusFilterToggle={toggleStatusFilter}
        statusOptions={STATUS_OPTIONS}
        handleEditLaborDraft={openEdit}
        onResetLabor={handleResetRow}
        onDetailClose={fetchData}
        onDoubleClickProject={onDoubleClickProject}
        me={me}
        info={info}
        tabVariant="labor"
        sortBy={sortBy}
        sortDir={sortDir}
        onSortChange={(nextSortBy, nextSortDir) => { setSortBy(nextSortBy); setSortDir(nextSortDir); }}
      />

      <Dialog open={!!editRow} onClose={closeEdit} maxWidth="xs" fullWidth>
        <DialogTitle>แก้ไขงบประมาณ / ผู้รับเงิน</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Autocomplete
              size="small"
              options={budgetOptions}
              value={editBudget}
              onChange={(_, val) => setEditBudget(val)}
              getOptionLabel={(o) => `${o?.prefix || ''}${o?.budget_number || ''} ${o?.name || ''}`.trim()}
              isOptionEqualToValue={(o, v) => (o?._id || o?.id) === (v?._id || v?.id)}
              renderInput={(params) => <TextField {...params} label="งบประมาณ (budget line)" size="small" />}
            />
            <TextField
              label="ชื่อผู้รับเงิน"
              size="small"
              value={editPayee.name}
              onChange={(e) => setEditPayee((p) => ({ ...p, name: e.target.value }))}
            />
            <TextField
              label="ธนาคาร"
              size="small"
              value={editPayee.bank}
              onChange={(e) => setEditPayee((p) => ({ ...p, bank: e.target.value }))}
            />
            <TextField
              label="เลขบัญชี"
              size="small"
              value={editPayee.account_number}
              onChange={(e) => setEditPayee((p) => ({ ...p, account_number: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit}>ยกเลิก</Button>
          <Button variant="contained" onClick={saveEdit} disabled={saving}>บันทึก</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
